<template>
    <common-card class="mt-3">
        <!-- Título -->
        <v-row class="px-6">
            <v-col cols="12">
                <h1>Script</h1>
            </v-col>
        </v-row>

        <!-- Fila 1: Campañas | Roles | Estatus -->
        <v-row class="px-6">
            <v-col cols="12" md="3">
                <v-autocomplete v-model="form.campaniaIds" label="Campañas" :items="campanias" item-text="compania"
                    item-value="id" multiple clearable outlined color="#00a7e4" :value-comparator="byId"
                    :menu-props="{ maxHeight: 320 }">
                    <!-- 1 chip + contador -->
                    <template v-slot:selection="{ item, index }">
                        <v-chip small color="primary" v-if="index === 0">
                            {{ item.compania }}
                        </v-chip>
                        <span v-else-if="index === 1" class="grey--text text-caption">
                            (+{{ form.campaniaIds.length - 1 }} más)
                        </span>
                    </template>
                </v-autocomplete>
            </v-col>

            <v-col cols="12" md="3">
                <v-autocomplete v-model="form.rolId" label="Roles" :items="roles" :item-text="roleText"
                    :item-value="roleValue" clearable outlined color="#00a7e4" />
            </v-col>

            <v-col cols="12" md="3">
                <v-autocomplete v-model="form.estatus" label="Estatus" :items="estatusOptions" item-text="text"
                    item-value="value" outlined color="#00a7e4" />
            </v-col>

            <!-- Tipo oculto (siempre GENERAL) -->
            <v-col cols="12" md="3" v-if="false">
                <v-autocomplete v-model="form.tipo" label="Tipo" :items="tipoOptions" item-text="text"
                    item-value="value" outlined color="#00a7e4" />
            </v-col>
        </v-row>

        <!-- Fila 2: Nombre -->
        <v-row class="px-6">
            <v-col cols="12">
                <v-text-field v-model="form.nombre" label="Nombre del Script" outlined color="#00a7e4" />
            </v-col>
        </v-row>

        <!-- CKEditor -->
        <v-row class="px-6 mb-8">
            <v-col cols="12">
                <label class="body-2 font-weight-medium mb-2">Diseño del Script</label>
                <ckeditor v-model="form.html" :config="editorConfig" />
            </v-col>
        </v-row>

        <!-- Botones -->
        <v-row class="px-6">
            <v-col cols="12" class="d-flex justify-end">
                <v-btn rounded outlined color="#00a7e4" dark class="mr-3"
                    @click="$router.push('/administacion/scripts')">
                    Cancelar
                </v-btn>
                <v-btn class="common-botton" dark :loading="saving" @click="guardar">
                    {{ isEdit ? "Guardar" : "Guardar" }}
                </v-btn>
            </v-col>
        </v-row>
    </common-card>
</template>

<script>
import CommonCard from "@/components/commonComponents/CommonCard.vue";
import ckeditor from "ckeditor4-vue";
import {
    createScript,
    getScript,
    updateScript,
} from "@/services/scripts/scripts.service.js";
import { getRoles } from "@/services/log/log.service.js";
import { getCampaingsList } from "@/services/campaings/campaing.service.js";

export default {
    components: { CommonCard, ckeditor: ckeditor.component },
    data() {
        return {
            editorConfig: { versionCheck: false },
            saving: false,
            form: {
                id: null,
                campaniaIds: [],
                rolId: null,
                estatus: "INACTIVO",
                tipo: "GENERAL",
                nombre: "",
                html: "",
            },
            campanias: [],
            campaniasIndex: {},
            roles: [],
            estatusOptions: [
                { text: "Activo", value: "ACTIVO" },
                { text: "Inactivo", value: "INACTIVO" },
            ],
            tipoOptions: [{ text: "General", value: "GENERAL" }],
        };
    },
    computed: {
        isEdit() {
            return !!this.$route.params.id;
        },
    },
    async mounted() {
        await Promise.all([this.fetchCampanias(), this.fetchRoles()]);
        if (this.isEdit) {
            await this.load();
            this.$nextTick(() => this.ensureCampaniasVisible());
        }
    },
    watch: {
        campanias() {
            this.ensureCampaniasVisible();
        },
    },
    methods: {
        byId(a, b) {
            const va = typeof a === "object" ? a?.id : a;
            const vb = typeof b === "object" ? b?.id : b;
            return Number(va) === Number(vb);
        },

        async fetchCampanias() {
            try {
                const res = await getCampaingsList({});
                const arr = Array.isArray(res?.campanias) ? res.campanias : [];

                this.campaniasIndex = arr.reduce((acc, c) => {
                    acc[Number(c.id)] = c;
                    return acc;
                }, {});

                // solo activas en el dropdown
                const activos = arr.filter((c) => String(c?.status) === "1");
                this.campanias = activos
                    .map((c) => ({
                        id: Number(c.id),
                        compania: c.medio || `#${c.id}`,
                        raw: c,
                    }))
                    .sort((a, b) => a.compania.localeCompare(b.compania));
            } catch (e) {
                console.error("Error cargando campañas", e);
                this.campanias = [];
                this.campaniasIndex = {};
            }
        },

        ensureCampaniasVisible() {
            const selected = (this.form.campaniaIds || [])
                .map((v) => Number(typeof v === "object" ? v.id : v))
                .filter(Number.isFinite);

            const existentes = new Set(this.campanias.map((c) => Number(c.id)));
            selected.forEach((id) => {
                if (!existentes.has(id)) {
                    const raw = this.campaniasIndex[id];
                    const label = raw?.medio || `Campaña #${id} (inactiva)`;
                    this.campanias.unshift({ id, compania: label, raw });
                    existentes.add(id);
                }
            });
        },

        async fetchRoles() {
            try {
                const raw = await getRoles();
                const list = Array.isArray(raw?.data)
                    ? raw.data
                    : Array.isArray(raw)
                        ? raw
                        : [];
                const titleCase = (s) =>
                    String(s)
                        .toLowerCase()
                        .replace(/_/g, " ")
                        .replace(/\b\w/g, (c) => c.toUpperCase());

                this.roles = list.map((r) => {
                    if (typeof r === "string") return { rol: r, nombre: titleCase(r) };
                    if (r?.rol)
                        return { rol: String(r.rol), nombre: r.nombre || titleCase(r.rol) };
                    if (r?.nombre) return { rol: String(r.nombre), nombre: r.nombre };
                    if (r?.id != null)
                        return { rol: String(r.id), nombre: r.nombre || titleCase(r.id) };
                    return { rol: String(r), nombre: String(r) };
                });
            } catch (e) {
                console.error("Error cargando roles", e);
                this.roles = [];
            }
        },

        async guardar() {
            this.saving = true;
            try {
                const payload = { ...this.form };

                payload.campaniaIds = (Array.isArray(payload.campaniaIds)
                    ? payload.campaniaIds
                    : []
                )
                    .map((x) => Number(typeof x === "object" ? x.id ?? x.value ?? x : x))
                    .filter(Number.isFinite);

                // limpieza compat
                delete payload.campaniaId;

                // rol como string o null
                payload.rolId = payload.rolId ? String(payload.rolId).trim() : null;

                if (this.isEdit) {
                    await updateScript(this.form.id || this.$route.params.id, payload);
                } else {
                    await createScript(payload);
                }
                this.$router.push("/administacion/scripts");
            } catch (e) {
                console.error("Error guardando script", e);
            } finally {
                this.saving = false;
            }
        },

        async load() {
            try {
                const resp = await getScript(this.$route.params.id);
                const s = resp?.data || resp;

                const ids = Array.isArray(s.campaniaIds)
                    ? s.campaniaIds
                    : s.campaniaId != null
                        ? [s.campaniaId]
                        : [];

                const norm = [
                    ...new Set(ids.map((x) => Number(x)).filter(Number.isFinite)),
                ];

                this.form = {
                    ...this.form,
                    ...s,
                    campaniaIds: norm,
                    rolId: s.rolId != null ? String(s.rolId) : null,
                    estatus: s.estatus || this.form.estatus,
                    tipo: "GENERAL",
                };

                this.$nextTick(() => this.ensureCampaniasVisible());
            } catch (e) {
                console.error("Error cargando script", e);
            }
        },

        // helpers de roles
        roleText(r) {
            if (!r) return "";
            if (r.nombre) return String(r.nombre);
            if (r.rol) return this.titleCase(r.rol);
            return String(r);
        },
        roleValue(r) {
            if (!r) return null;
            if (r.rol) return String(r.rol);
            if (r.nombre) return String(r.nombre);
            return String(r);
        },
        titleCase(s) {
            return String(s)
                .toLowerCase()
                .replace(/_/g, " ")
                .replace(/\b\w/g, (c) => c.toUpperCase());
        },
    },
};
</script>